import type { Device, DeviceDashboardResponse } from "../devices/types";
import { getDeviceDisplayName, getFunctions, isDeviceOnlineNow, parseMetaJson } from "../devices/adapters";
import type { Machine, MachineSlot } from "./types";

export const DEV_MACHINE_ID = "main";
export type DeviceStructure = Pick<DeviceDashboardResponse, "device" | "currentMeta">;
type SummarySource = DeviceStructure & Partial<Pick<DeviceDashboardResponse, "latestPorts">>;

function countChannels(dashboard: SummarySource): number {
  if (dashboard.latestPorts !== undefined) return dashboard.latestPorts.length;
  return new Set(getFunctions(parseMetaJson(dashboard.currentMeta).data)
    .filter((fn) => fn.enabled !== false && typeof fn.p === "number" && Number.isInteger(fn.p) && fn.p >= 0)
    .map((fn) => fn.p)).size;
}

export interface MachineSummary {
  id: string;
  workspaceId: string;
  name: string;
  deviceCount: number;
  channelCount: number;
  lastSeenAt: string | null;
  status: "online" | "offline" | "neutral";
}

export interface MachineDashboard {
  machine: Machine;
  dashboards: DeviceDashboardResponse[];
  channelCount: number;
  lastSeenAt: string | null;
}

export interface MachineSidebarGateway {
  deviceId: string;
  mqttId: string;
  /** Nome operacional, quando alguem nomeou. Ver `nomeDeExibicao`. */
  nomeFantasia?: string | null;
  slotIndex: number;
  channelCount: number;
}

export interface MachineSidebarSummary {
  workspaceId: string;
  gateways: MachineSidebarGateway[];
}

export function buildDevMachine(workspaceId: string, devices: Device[]): Machine {
  return {
    id: DEV_MACHINE_ID,
    workspaceId,
    name: "Maquina principal",
    createdAt: devices[0]?.firstSeenAt ?? new Date(0).toISOString(),
    slots: devices.map<MachineSlot>((device, index) => ({
      machineId: DEV_MACHINE_ID,
      slotIndex: index,
      deviceId: device.id,
      boundAt: device.firstSeenAt ?? new Date(0).toISOString(),
      alias: device.mqttId,
    })),
  };
}

export function buildMachineSummary(
  workspaceId: string,
  dashboards: SummarySource[],
): MachineSummary {
  const devices = dashboards.map((dashboard) => dashboard.device);
  const channelCount = dashboards.reduce(
    (total, dashboard) => total + countChannels(dashboard),
    0,
  );
  const lastSeenAt = getLatestDate(devices.map((device) => device.lastSeenAt ?? null));

  return {
    id: DEV_MACHINE_ID,
    workspaceId,
    name: devices.length === 1 ? getDisplayNameFromDashboard(dashboards[0]) : "Maquina principal",
    deviceCount: devices.length,
    channelCount,
    lastSeenAt,
    status: devices.some((device) => isDeviceOnlineNow(device))
      ? "online"
      : devices.length > 0
        ? "offline"
        : "neutral",
  };
}

export function buildMachineDashboard(
  workspaceId: string,
  dashboards: DeviceDashboardResponse[],
): MachineDashboard {
  const machine = buildDevMachine(
    workspaceId,
    dashboards.map((dashboard) => dashboard.device),
  );
  return {
    machine,
    dashboards,
    channelCount: dashboards.reduce(
      (total, dashboard) => total + (dashboard.latestPorts?.length ?? 0),
      0,
    ),
    lastSeenAt: getLatestDate(
      dashboards.map((dashboard) => dashboard.device.lastSeenAt ?? null),
    ),
  };
}

export function buildMachineSidebarSummary(
  workspaceId: string,
  dashboards: SummarySource[],
): MachineSidebarSummary {
  return {
    workspaceId,
    gateways: dashboards.map((dashboard, slotIndex) => ({
      deviceId: dashboard.device.id,
      mqttId: dashboard.device.mqttId,
      nomeFantasia: dashboard.device.nomeFantasia ?? null,
      slotIndex,
      channelCount: countChannels(dashboard),
    })),
  };
}

function getDisplayNameFromDashboard(dashboard: DeviceStructure): string {
  const parsed = parseMetaJson(dashboard.currentMeta);
  return getDeviceDisplayName(dashboard.device, parsed.data);
}

function getLatestDate(values: Array<string | null>): string | null {
  return (
    values
      .filter((value): value is string => !!value)
      .sort((left, right) => right.localeCompare(left))[0] ?? null
  );
}
